import {useCallback, useContext, useMemo} from "react";

import type {Action, State} from "./content-cache-types.ts";
import {
    ListedContentCacheContext,
    UnlistedContentCacheContext,
    type ContentCacheContextType,
} from "./content-cache.tsx";
import {mergeState} from "./utils.ts";

const clearCacheAction = {
    type: "CLEAR_CONTENT_CACHE",
} as const;

/**
 * useContentCache is a hook that gives components access to the listed content
 * cache and the unlisted content cache.
 */
const useContentCache = (): ContentCacheContextType => {
    const {state: listedState, dispatch: listedDispatch} =
        useContext<ContentCacheContextType>(ListedContentCacheContext);

    const {state: unlistedState, dispatch: unlistedDispatch} =
        useContext<ContentCacheContextType>(UnlistedContentCacheContext);

    const state: State = useMemo(
        () => mergeState(unlistedState, listedState),
        [listedState, unlistedState],
    );

    const dispatch = useCallback(
        (action: Action) => {
            if (action.type !== "CLEAR_CONTENT_CACHE") {
                if (
                    listedState.publishedContentVersion &&
                    action.publishedContentVersion !==
                        listedState.publishedContentVersion
                ) {
                    listedDispatch(clearCacheAction);
                }
                if (
                    unlistedState.publishedContentVersion &&
                    action.publishedContentVersion !==
                        unlistedState.publishedContentVersion
                ) {
                    unlistedDispatch(clearCacheAction);
                }
            }
            if (action.unlisted) {
                unlistedDispatch(action);
            } else {
                listedDispatch(action);
            }
        },
        [
            listedDispatch,
            unlistedDispatch,
            listedState.publishedContentVersion,
            unlistedState.publishedContentVersion,
        ],
    );

    return {state, dispatch};
};

export default useContentCache;
